import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft, IconChartArrowsVertical, IconTrendingUp, IconLoader2, IconUser, IconBook } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function Evidencia() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/evidence"); setData(data); }
    catch (e) { setError(formatApiError(e)); }
  })(); }, []);

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="evidencia-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Evidencia · Cursos → Impacto en estudiantes" : "Evidence · Courses → Student impact"}</span>
      </div>

      <div className="wrap-app max-w-[1080px] py-10">
        <p className="text-muted text-[14px] mb-6 leading-relaxed max-w-[620px]">
          {lang === "es"
            ? "Cada docente aparece con los cursos aprobados en Aula y los estudiantes vinculados en Círculo. El impacto se estima con señales positivas (logros, avance, regulación) menos señales negativas (crisis, dificultad) registradas en la bitácora."
            : "Each teacher appears with completed Aula courses and their linked Círculo students. Impact estimates positive (achievement, progress) minus negative (crisis, difficulty) log signals."}
        </p>
        {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
        {!data ? (
          <div className="card"><IconLoader2 className="animate-spin" /></div>
        ) : data.docentes.length === 0 ? (
          <p className="text-tert text-[13px]" data-testid="evidencia-empty">
            {lang === "es" ? "Aún no hay docentes con cursos completados y estudiantes vinculados." : "No teachers with completed courses linked to students yet."}
          </p>
        ) : (
          <div className="space-y-4">
            {data.docentes.map((d) => (
              <div key={d.docente_id} className="card" data-testid={`evidencia-docente-${d.docente_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <IconUser size={16} className="text-teal" />
                      <h3 className="font-display text-[18px] font-medium">{d.docente_name}</h3>
                    </div>
                    <p className="text-tert text-[12.5px] mt-0.5">{d.docente_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="lbl m-0">{lang === "es" ? "Impacto agregado" : "Aggregate impact"}</p>
                    <p className={`font-display text-[24px] font-medium ${d.aggregate_impact >= 0 ? "text-oktext" : "text-errtext"}`}>
                      {d.aggregate_impact >= 0 ? "+" : ""}{d.aggregate_impact}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="lbl m-0 flex items-center gap-2"><IconBook size={12} /> {lang === "es" ? "Cursos completados" : "Completed courses"} ({d.courses_completed})</p>
                    <ul className="mt-2 space-y-1">
                      {d.course_titles.map((t, i) => (
                        <li key={i} className="text-[13px] text-muted flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-teal-deep inline-block" />
                          {t}
                        </li>
                      ))}
                      {d.course_titles.length === 0 && <li className="text-tert text-[12px]">—</li>}
                    </ul>
                  </div>
                  <div>
                    <p className="lbl m-0 flex items-center gap-2"><IconTrendingUp size={12} /> {lang === "es" ? "Estudiantes vinculados" : "Linked students"} ({d.linked_children.length})</p>
                    <div className="mt-2 space-y-2">
                      {d.linked_children.map((c) => (
                        <div key={c.child_id} className="border border-line rounded-lg p-2.5">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-[13.5px]">{c.child_name}</p>
                            <span className={`tag ${c.impact_score >= 0 ? "bg-okbg text-oktext" : "bg-errbg text-errtext"}`}>
                              {c.impact_score >= 0 ? "+" : ""}{c.impact_score}
                            </span>
                          </div>
                          <p className="text-tert text-[11.5px] mt-0.5">
                            {c.condition || "—"} · <span className="text-oktext">+{c.positive_signals}</span> · <span className="text-errtext">−{c.negative_signals}</span>
                          </p>
                        </div>
                      ))}
                      {d.linked_children.length === 0 && <p className="text-tert text-[12px]">—</p>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
